import { PHASE, PLAYER } from "../../../common/constants.ts";
import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import { player, team } from "../index.ts";
import {
	getLastRealTid,
	proveItAmount,
	proveItStuck,
	proveItWorthKeeping,
} from "./proveIt.ts";

// Backstop run right after autoSign: any genuinely rosterable young/valuable free agent who has
// lingered unsigned gets kept by his previous team on a short prove-it deal (Bird rights, so it's
// allowed over the cap). This mirrors reality -- no team lets a good young player rot in free
// agency; worst case they keep him on a cheap one-year "prove-it" deal. If his old team is full or
// gone, he's left in the pool at his now-signable (caved) demand for autoSign to clear normally.
const resolveLingeringFreeAgents = async () => {
	const players = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.FREE_AGENT,
	);
	if (players.length === 0) {
		return;
	}

	const maxRosterSize = g.get("maxRosterSize");
	const userTids = g.get("userTids");
	const canControlUserTeam = !!local.autoPlayUntil || g.get("spectator");

	for (const p of players) {
		if (!proveItStuck(p)) {
			continue;
		}

		const amount = proveItAmount(p);
		if (!proveItWorthKeeping(amount)) {
			// Replacement level -- let the normal min-contract market handle him.
			continue;
		}

		const tid = getLastRealTid(p);
		if (tid === undefined || tid < 0) {
			continue;
		}

		// Leave the user's own free agents for the user to decide (unless auto-playing/spectating).
		if (userTids.includes(tid) && !canControlUserTeam) {
			continue;
		}

		const t = await idb.cache.teams.get(tid);
		if (!t || t.disabled) {
			continue;
		}

		const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
		if (roster.length >= maxRosterSize) {
			continue;
		}

		// Bird rights: the incumbent may exceed the cap to retain its own player, so there's
		// deliberately no salary-cap check here. Short term + the proveIt flag make him unhappy (he
		// won't want to re-sign and the AI is biased to trade him -- see moodComponents and
		// trade/betweenAiTeams).
		const contract = {
			amount,
			exp:
				g.get("season") +
				g.get("minContractLength") +
				(g.get("phase") <= PHASE.PLAYOFFS ? -1 : 0),
			proveIt: true as const,
		};

		await player.sign(p, tid, contract, g.get("phase"));
		await idb.cache.players.put(p);
		await team.rosterAutoSort(tid);
	}
};

export default resolveLingeringFreeAgents;
